import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { IMAGES } from "@/data/clinic";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";

export default function About() {
    return (
        <section id="marma" data-testid="about-section" className="bg-cream py-20 md:py-32">
            <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
                <div>
                    <SectionHeading
                        overline="Marma Chikitsa"
                        title="The Ancient Science of Vital Points"
                        testId="about-heading"
                    >
                        Marma Chikitsa is a gentle practice described in classical Ayurvedic texts,
                        centred on <em className="font-serif italic">marma</em> — vital points where,
                        according to tradition, the body's energies meet. Classical Ayurvedic literature
                        describes 107 such points across the body.
                    </SectionHeading>

                    <Reveal delay={0.15}>
                        <p className="mt-5 text-sm leading-relaxed text-moss md:text-base">
                            At Agastya Chiro and Ancient Marma Therapy in Kurnool, every session is
                            unhurried and personal. We begin by listening, then work with light, precise
                            touch — often with warm herbal oils in the Abhyanga tradition — always at a
                            pace and pressure you are comfortable with.{" "}
                            <Link
                                to="/marma-chikitsa"
                                data-testid="about-guide-link"
                                className="group inline-flex items-center gap-1 font-semibold text-forest underline decoration-terracotta/50 underline-offset-4 transition-colors duration-300 hover:text-terracotta"
                            >
                                Read our beginner's guide to Marma Chikitsa
                                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                            </Link>
                        </p>
                    </Reveal>

                    <Reveal delay={0.25}>
                        <div className="mt-8 flex gap-4 rounded-2xl border border-sand bg-bone p-6">
                            <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-forest" />
                            <div>
                                <p className="text-sm font-semibold text-ink">
                                    Tradition, communicated responsibly
                                </p>
                                <p className="mt-1.5 text-sm leading-relaxed text-moss">
                                    Our approaches come from classical Ayurvedic tradition. Modern
                                    scientific research on marma therapy is still developing, and we
                                    present it as a complementary wellness practice — never as a
                                    replacement for medical diagnosis or treatment.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.1} className="relative">
                    <div className="overflow-hidden rounded-3xl">
                        <img
                            src={IMAGES.marmaTherapy}
                            alt="Therapist working with gentle pressure in a traditional wellness session"
                            loading="lazy"
                            decoding="async"
                            className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                        />
                    </div>
                    <div className="absolute -bottom-8 -left-4 max-w-[240px] rounded-2xl bg-forest p-6 text-cream shadow-xl md:-left-10">
                        <p className="font-serif text-4xl font-medium text-gold">107</p>
                        <p className="mt-1 text-xs leading-relaxed text-cream/80">
                            marma points described in classical Ayurvedic texts
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
